import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const DEBOUNCE_DELAY = 500;
const EVENT_NAMESPACE = "automationInitialDetails";
// Kept whole, "*/*" included: the create action renders an HTML partial into its JSON body
// (gift_templates_form_html), and narrowing this to :json makes the partial go missing. No
// X-Requested-With either - this action never branches on format, and the missing XHR flag is
// what keeps the fallback format at :html.
const ACCEPT = "application/json, text/plain, */*";
const YARDI_AUTOMATION_TYPES = ["property_move_in", "lease_renewal"];

export default class extends Controller {
  static get targets() {
    return ["form", "automationType", "recipientsLocation", "contactsLists", "milestoneYearsInput", "formContainer"];
  }

  static get classes() {
    return ["birthday", "workAnniversary", "propertyMoveIn", "leaseRenewal"];
  }

  connect() {
    this.debounceTimeout = null;
    this.isRequestInProgress = false;

    this.updateDefaultAutomationNameOnAdditionalSettings("birthday");

    $(this.automationTypeTarget).select2({ minimumResultsForSearch: Infinity });
    $(this.contactsListsTarget).select2({ minimumResultsForSearch: Infinity, closeOnSelect: false, width: "resolve" });
    $(this.recipientsLocationTarget).select2({ minimumResultsForSearch: Infinity, placeholder: "Select" });

    // select2 announces every one of these through jQuery's own event bus -- a plain
    // .trigger("change")/"select2:select"/"select2:close" that never reaches a Stimulus
    // data-action -- so the bindings stay on jQuery's side, namespaced for a clean disconnect().
    $(this.automationTypeTarget).on(`change.${EVENT_NAMESPACE}`, () => this.onAutomationTypeChange());
    $(this.contactsListsTarget).on(`select2:select.${EVENT_NAMESPACE}`, (event) => this.onContactsListSelect(event));
    $(this.contactsListsTarget).on(`change.${EVENT_NAMESPACE}`, () => this.styleSelectedOptions());
    $(this.contactsListsTarget).on(`select2:close.${EVENT_NAMESPACE}`, () => this.checkFormCompletion());
    $(this.recipientsLocationTarget).on(`change.${EVENT_NAMESPACE}`, () => {
      this.hideAdditionalSettings();
      this.checkFormCompletion();
    });
    $(this.milestoneYearsInputTarget).on(`input.${EVENT_NAMESPACE} blur.${EVENT_NAMESPACE}`, () => this.checkFormCompletion());

    this.styleSelectedOptions();
  }

  disconnect() {
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); }

    $(this.automationTypeTarget).off(`.${EVENT_NAMESPACE}`);
    $(this.contactsListsTarget).off(`.${EVENT_NAMESPACE}`);
    $(this.recipientsLocationTarget).off(`.${EVENT_NAMESPACE}`);
    $(this.milestoneYearsInputTarget).off(`.${EVENT_NAMESPACE}`);
  }

  onAutomationTypeChange() {
    const automationType = this.automationTypeTarget.value;
    const milestoneDropdown = this.milestoneYearsInputTarget.closest(".milestone-years-dropdown");

    if (automationType === "work_anniversary") {
      milestoneDropdown.classList.remove("hidden");
      this.milestoneYearsInputTarget.value = "";
      document.querySelector(".gift-templates-wrapper")?.classList.add("hidden");
    } else {
      milestoneDropdown.classList.add("hidden");
      this.milestoneYearsInputTarget.value = "";
    }

    this.contactsListsTarget.disabled = false;
    this.formContainerTarget.classList.remove(this.birthdayClass, this.workAnniversaryClass, this.propertyMoveInClass, this.leaseRenewalClass);

    switch (automationType) {
      case "birthday":
        this.formContainerTarget.classList.add(this.birthdayClass);
        break;
      case "work_anniversary":
        this.formContainerTarget.classList.add(this.workAnniversaryClass);
        break;
      case "property_move_in":
        this.formContainerTarget.classList.add(this.propertyMoveInClass);
        break;
      case "lease_renewal":
        this.formContainerTarget.classList.add(this.leaseRenewalClass);
        break;
    }

    // select defaults for yardi automations
    if (YARDI_AUTOMATION_TYPES.includes(automationType)) {
      const yardiOption = Array.from(this.contactsListsTarget.options).find((option) => option.text.includes("Yardi"));

      if (yardiOption) {
        // select2 announces the resulting UI refresh through jQuery's own event bus (see the
        // connect() comment) -- val()+trigger("change") has to stay jQuery for select2 to notice.
        $(this.contactsListsTarget).val(yardiOption.value).trigger("change");
        this.contactsListsTarget.disabled = true;

        $(this.recipientsLocationTarget).val("domestic").trigger("change");
      }
    }

    this.updateDefaultAutomationNameOnAdditionalSettings(automationType);
    this.toggleMinimumLeaseTermOnAdditionalSettings(automationType);
    this.hideAdditionalSettings();
    this.checkFormCompletion();
  }

  onContactsListSelect(event) {
    const optionValue = event.params.data.id;
    const currentValues = $(this.contactsListsTarget).val() || [];

    if (optionValue === "-1") {
      if (currentValues.length > 1) {
        $(this.contactsListsTarget).val(["-1"]).trigger("change");
        $(this.contactsListsTarget).select2("close");
      }
    } else if (currentValues.includes("-1")) {
      const newValues = currentValues.filter((val) => val !== "-1");

      $(this.contactsListsTarget).val(newValues).trigger("change");
      $(this.contactsListsTarget).select2("close");
      setTimeout(() => $(this.contactsListsTarget).select2("open"), 1);
    }
  }

  styleSelectedOptions() {
    Array.from(this.contactsListsTarget.selectedOptions).forEach((option) => {
      const colorClass = option.dataset.color;

      // Scoped to this.element, matching the original jQuery lookup's scoping --
      // .select2-selection__choice is select2's own generated markup, not ours.
      this.element.querySelectorAll(`.contacts-list-dropdown .select2 .select2-selection__choice[title='${CSS.escape(option.text)}']`)
        .forEach((el) => el.classList.add(colorClass));
    });
  }

  checkFormCompletion() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    let allFilled = true;

    this.formTarget.querySelectorAll("select[required]:not(:disabled)").forEach((select) => {
      if (select.multiple) {
        if (select.selectedOptions.length === 0) { allFilled = false; }
      } else if (!select.value) {
        allFilled = false;
      }
    });

    // Check milestone years text input when work anniversary is selected
    if (this.automationTypeTarget.value === "work_anniversary") {
      if (!this.milestoneYearsInputTarget.value || this.milestoneYearsInputTarget.value.trim() === "") {
        allFilled = false;
      }
    }

    if (allFilled) {
      this.debounceTimeout = setTimeout(() => {
        this.submitForm();
        this.debounceTimeout = null;
      }, DEBOUNCE_DELAY);
    }
  }

  submitForm() {
    if (this.isRequestInProgress) { return; }

    this.isRequestInProgress = true;

    // Once validated, add contact lists select back in case it was disabled for a yardi automation
    this.contactsListsTarget.disabled = false;

    const formData = new URLSearchParams(new FormData(this.formTarget)).toString();
    const formAction = this.formTarget.getAttribute("action");
    const formMethod = this.formTarget.getAttribute("method") || "POST";

    // Disable contact lists select if yardi automation to prevent changes after submission
    this.contactsListsTarget.disabled = YARDI_AUTOMATION_TYPES.includes(this.automationTypeTarget.value);

    const options = RequestHelper.defaultRequestOptions();
    options.method = formMethod;
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/x-www-form-urlencoded",
      "Accept": ACCEPT
    });
    options.body = formData;

    fetch(formAction, options).
      then((response) => {
        if (!response.ok) { throw new Error(`Request failed with status ${response.status}`); }
        return response.json();
      }).
      then((data) => {
        const automationsContainer = document.querySelector(".automations-container");

        // data.gift_templates_form_html is server-rendered from our own origin (see the ACCEPT
        // comment above), not user/response-controlled text -- trusted markup, kept on jQuery
        // since .replaceWith(htmlString) parsing semantics aren't a mechanical retreat (D4).
        $(".gift-templates-wrapper").replaceWith(data.gift_templates_form_html);

        const newContent = document.querySelector(".gift-templates-wrapper");

        if (!data.editing) {
          window.history.pushState({}, "", "/automations/gift-templates");
        }

        newContent.classList.remove("hidden");

        if (automationsContainer.scrollWidth > automationsContainer.clientWidth) {
          automationsContainer.classList.add("enable-scrolling");
        } else {
          automationsContainer.classList.remove("enable-scrolling");
        }
      }).
      catch((error) => {
        console.error("Form submission error:", error);
      }).
      finally(() => {
        this.isRequestInProgress = false;
      });
  }

  updateDefaultAutomationNameOnAdditionalSettings(automationType) {
    const isDefaultMode = document.querySelector(".additional-settings-wrapper .name-editor[data-default='true']") != null;
    if (!isDefaultMode) { return; }

    const automationNameEditor = document.querySelector(".additional-settings-wrapper .single-field-editor .normal-mode .name");
    if (!automationNameEditor) { return; }

    switch (automationType) {
      case "birthday":
        automationNameEditor.textContent = "Send Birthday Gifts";
        break;
      case "work_anniversary":
        automationNameEditor.textContent = "Send Work Anniversary Gifts";
        break;
      case "property_move_in":
        automationNameEditor.textContent = "Send Move In Gifts";
        break;
      case "lease_renewal":
        automationNameEditor.textContent = "Send Renewal Gifts";
        break;
    }
  }

  // The additional settings form is rendered once on page load, so the minimum lease term field
  // is always there for yardi organizations and only shown for renewal automations
  toggleMinimumLeaseTermOnAdditionalSettings(automationType) {
    document.querySelectorAll(".additional-settings-wrapper .minimum-lease-term-setting").forEach((el) => {
      el.classList.toggle("hidden", automationType !== "lease_renewal");
    });
  }

  hideAdditionalSettings() {
    document.querySelector(".additional-settings-wrapper")?.classList.add("hidden");
  }
}
